import { Icon, Label } from "semantic-ui-react";

import { CircleIcon, getAppointmentPinColor, useSelectedLocation } from "components";
import { APPOINTMENT_NOTE_TYPES, DBB_STATUSES } from "models";
import { useCarBrandsQuery } from "modules/AppointmentDetails/hooks";
import { CallCustomerNote, MainNote, NoDriveNote, RecurringCarNote } from "modules/Dayplanner/components/AppointmentCard/components/NotificationIcons/components";
import { isDateAfter } from "utils/dateHelperFunctions";

import type { Appointment, AppointmentNote } from "models";
import type { SemanticCOLORS } from "semantic-ui-react";

type NotificationIconsProps = {
  appointment: Appointment;
};

export type NoteProps = {
  note: AppointmentNote;
};

const statusColorMap: { [key in DBB_STATUSES]: SemanticCOLORS } = {
  [DBB_STATUSES.NoDBBStatus]: "red",
  [DBB_STATUSES.DBBPushed]: "green",
  [DBB_STATUSES.DBBIsPending]: "orange",
  [DBB_STATUSES.DBBFailed]: "red"
};

export const NotificationIcons = ({ appointment }: NotificationIconsProps) => {
  const mainNote = appointment.notes?.find(n => n.appointment_note_type_id === APPOINTMENT_NOTE_TYPES.Main);
  const callCustomerNote = appointment.notes?.find(n => n.appointment_note_type_id === APPOINTMENT_NOTE_TYPES.CallCustomer);
  const recurringCarNote = appointment.notes?.find(n => n.appointment_note_type_id === APPOINTMENT_NOTE_TYPES.RecurringCar);
  const noDriveNote = appointment.notes?.find(n => n.appointment_note_type_id === APPOINTMENT_NOTE_TYPES.NoDrive);

  const checkedTireLabelColor = statusColorMap[appointment.final_car_check_dbb_status];
  const bigTireLabelColor = statusColorMap[appointment.car_check_dbb_status];

  const { selectedLocation } = useSelectedLocation();
  const { data: brands = [] } = useCarBrandsQuery();
  const pinColor = getAppointmentPinColor(appointment, brands, selectedLocation);

  return (
    <div className="NotificationIcons">
      {mainNote && <MainNote note={mainNote} />}
      {callCustomerNote && <CallCustomerNote note={callCustomerNote} />}
      {noDriveNote && appointment.car_in_shop && !appointment.car_out_of_shop && <NoDriveNote note={noDriveNote} />}

      {appointment.has_dbb && appointment.dbb_appointment_date && (
        <>
          {appointment.final_car_check_dbb_status > DBB_STATUSES.NoDBBStatus && (
            <Label className="checkedTire" circular color={checkedTireLabelColor}>
              <Icon className="tire" />
              <Icon className="check" />
            </Label>
          )}
          {appointment.final_car_check_dbb_status === DBB_STATUSES.NoDBBStatus && appointment.car_check_dbb_status > DBB_STATUSES.NoDBBStatus && (
            <Label className="bigTire" circular color={bigTireLabelColor}>
              <Icon className="tire" />
            </Label>
          )}
          {appointment.final_car_check_dbb_status === DBB_STATUSES.NoDBBStatus && appointment.car_check_dbb_status === DBB_STATUSES.NoDBBStatus && (
            <Label className="bigTire" circular color={isDateAfter(new Date(), appointment.dbb_appointment_date) ? "green" : "orange"}>
              <Icon className="tire" />
            </Label>
          )}
        </>
      )}

      {(appointment.is_recurring || recurringCarNote) && <RecurringCarNote note={recurringCarNote} />}
      {appointment.customer_waiting && <CircleIcon icon="coffee" />}
      {appointment.is_money && <CircleIcon icon="is_money" />}
      {appointment.is_star && <CircleIcon icon="is_star" color={appointment.is_star_color} />}
      {appointment.is_shop && <CircleIcon icon="is_shop" color={appointment.is_shop_color} />}

      {appointment.has_extra_check && (
        <Label circular color="blue">
          <Icon className="fill drip regular" />
        </Label>
      )}

      {appointment.num_approved_items_not_fixed > 0 && (
        <span className="-fix-items-count">
          <Label circular color="red">
            {appointment.num_approved_items_not_fixed}
          </Label>
        </span>
      )}

      {appointment.final_check_has_remarks && <CircleIcon icon="wrench" />}

      {(appointment.recall_pin_count > 0 || appointment.remark_pin_count > 0 || appointment.warranty_pin_count > 0) && (
        <CircleIcon icon={appointment.warranty_pin_count > 0 ? "shield" : "pin"} color={pinColor} />
      )}

      {((appointment.key_dropped_at && !appointment.key_picked_up_at) ||
        (appointment.sharebox_key_dropped_at && !appointment.sharebox_key_picked_up_at) ||
        (appointment.key_dropped_back_at && !appointment.key_picked_up_back_at) ||
        (appointment.sharebox_key_dropped_back_at && !appointment.sharebox_key_picked_up_back_at)) && (
        <Label circular className="keylocker-icon">
          <Icon name="key" />
        </Label>
      )}
    </div>
  );
};
